import { action, computed, observable } from 'mobx'
import { catchError, combineLatest, EMPTY } from 'rxjs'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import type { ListRolesFn, SearchUsersFn } from '@/features/iam'
import type { UserDataType } from '@/features/iam/users/UserData'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { SelectFilter } from '@/infra/SelectFilter'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorageFactory,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { debounceTime, switchMap } from 'rxjs/operators'
import { type Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { tapAction } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * User types as the filter storage persists them. Saved filters hold these
 * values, so changing them drops users' saved filters.
 */
const storedUserTypes = {
  ADMIN: 'Admin',
  USER: 'User',
  PRO: 'Pro',
  CALL_AGENT: 'CallAgent',
} as const satisfies Record<string, UserDataType>

type StoredUserType = keyof typeof storedUserTypes

interface UserFilter {
  workspace?: Workspace | null
  userTypes?: StoredUserType[] | null
  inactive?: boolean | null
  roles?: Id[] | null
}

type UserSearchFilter = UserFilter & { searchText?: string }

const userTypeOptions: FilterItem<StoredUserType>[] = [
  {
    title: 'Admin',
    value: 'ADMIN',
  },
  {
    title: 'User',
    value: 'USER',
  },
  {
    title: 'Pro',
    value: 'PRO',
  },
  {
    title: 'Call Agent',
    value: 'CALL_AGENT',
  },
]

export class UserSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly searchText = new SearchTextViewModel(500)

  readonly filterStorage

  @observable
  accessor totalUsers = 0

  readonly filters

  readonly scrollProvider = new InfiniteScrollProvider<
    SearchableUser,
    UserSearchFilter
  >({
    limit: 100,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading users failed',
        signal,
      )
      return withRetry(() =>
        this._searchUsers(
          {
            searchText: filter?.searchText,
            limit: paging.limit,
            after: paging.after,
            workspaceId: filter?.workspace?.id,
            userTypes: filter?.userTypes?.map((t) => storedUserTypes[t]),
            inactive: filter?.inactive,
            roleIds: filter?.roles ?? undefined,
          },
          signal,
        ),
      )
    },
  })

  private readonly _query$

  constructor(
    private readonly _searchUsers: SearchUsersFn,
    private readonly _listRoles: ListRolesFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _setDefaultWorkspace: () => void,
  ) {
    this.filterStorage = this._createFilterStorage('UserManagementTableFilters')

    this.filters = new FiltersViewModel<UserFilter>({
      filterStorage: this.filterStorage,
      definitions: {
        workspace: {
          title: 'Workspace',
          implicit: true,
          persist: false,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              Workspace.none,
              FilterItemSource.static([]),
            ),
        },
        userTypes: {
          icon: 'users',
          title: 'User types',
          persist: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(userTypeOptions),
              true,
            ),
        },
        inactive: {
          icon: 'pending',
          title: 'Inactive',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              false,
              FilterItemSource.static([]),
            ),
        },
        roles: {
          icon: 'roles',
          title: 'Roles',
          persist: false,
          isVisible: (): boolean => this.shouldShowRolesFilter,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              async () => this._fetchRoleItems(),
              true,
            ),
        },
      },
    })

    this._query$ = combineLatest([
      this.filters.appliedFilters$,
      this.searchText.debouncedValue$,
    ]).pipe(
      // The query subscribes on activation before the saved filters load and
      // the default workspace applies; waiting a tick makes the first fetch
      // use them instead of the previous workspace filter.
      debounceTime(0),
      switchMap(([appliedFilters, searchText]) =>
        withAbortSignal((signal) =>
          this.scrollProvider.fetch({
            filter: { searchText, ...appliedFilters },
            signal,
          }),
        ).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      tapAction((result) => (this.totalUsers = result.total ?? 0)),
    )

    this._lifecycle.manage(this._query$)
    this._lifecycle.manage(this.filters)
  }

  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace
    >
  }

  @computed
  get isFetchingInitial() {
    return (
      this.scrollProvider.fetch.pending && !this.scrollProvider.isRevalidating
    )
  }

  @computed
  get userInactiveFilter() {
    return this.filters.getFilter('inactive') as SingleSelectFilter<
      string,
      boolean
    >
  }

  activate() {
    this._lifecycle.activate()
    this._setDefaultWorkspace()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  removeUsers(userIds: Id[]) {
    this.scrollProvider.removeWhere((user) => userIds.includes(user.publicId))
  }

  @computed
  get shouldShowRolesFilter() {
    return Boolean(this.workspaceFilter.value.id)
  }

  clearWorkspaceScopedFilters() {
    this.filters.clearFilter('roles')
  }

  private async _fetchRoleItems(): Promise<FilterItem<Id>[]> {
    if (!this.workspaceFilter.value) {
      return []
    }

    const roles = await this._listRoles(
      { workspaceId: this.workspaceFilter.value.id },
      this._lifecycle.signal,
    )

    return roles.map((r) => {
      return { title: r.name, description: r.description, value: r.id }
    })
  }
}
